import * as React from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';

import useAxios from '../utils/useAxios';

export type ClientForm = { name: string; lastname: string; email: string; phone: string; address: string };
const EMPTY: ClientForm = { name: '', lastname: '', email: '', phone: '', address: '' };

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: (client: any) => void;
  /** Pass an existing client to edit it; omit to create. */
  client?: any;
};

const ClientFormDialog = ({ open, onClose, onSaved, client }: Props) => {
  const axios = useAxios();
  const [form, setForm] = React.useState<ClientForm>(EMPTY);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setForm(client ? { ...EMPTY, ...Object.fromEntries(Object.entries(client).map(([k, v]) => [k, v ?? ''])) } as ClientForm : EMPTY);
      setError('');
    }
  }, [open, client]);

  const set = (key: keyof ClientForm) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.lastname.trim()) {
      setError('Nombre y apellido son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      lastname: form.lastname.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
    };
    try {
      const { data } = client
        ? await axios.put(`api/clients/${client.id}/`, payload)
        : await axios.post('api/clients/', payload);
      onSaved(data);
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(typeof data === 'object' && data ? Object.values(data).flat().join(' ') : 'No se pudo guardar el cliente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ component: 'form', onSubmit: save } as any}>
      <DialogTitle>{client ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Nombre" required fullWidth autoFocus value={form.name} onChange={set('name')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Apellido" required fullWidth value={form.lastname} onChange={set('lastname')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Correo electrónico" type="email" fullWidth value={form.email} onChange={set('email')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Teléfono" type="tel" fullWidth value={form.phone} onChange={set('phone')} />
          </Grid>
          <Grid size={12}>
            <TextField label="Dirección" fullWidth value={form.address} onChange={set('address')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {client ? 'Guardar cambios' : 'Crear cliente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFormDialog;
